import * as PopoverPrimitive from "@radix-ui/react-popover";
import classNames from "classnames";
import { computed, IComputedValue } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useEffect, useRef, useState } from "react";
import {
  ActionType,
  EvaluateExpressionType,
  Pane,
  PaneRenderOutput,
  PaneType,
} from "../Types";
import { FeatherIcon } from "../ui/FeatherIcon";
import { Tooltip } from "../ui/Tooltip";
import { assert } from "../Utils";
import { canvasDispatch } from "./CanvasDispatch";
import { showPaneSettingsPaneIdAtom } from "./CanvasState";

function areRenderOutputTypesEqual(
  a: PaneRenderOutput | undefined,
  b: PaneRenderOutput | undefined
): boolean {
  if (a === undefined && b === undefined) {
    return true;
  }
  return a !== undefined && b !== undefined && a[0] === b[0];
}

function SettingsRenderOutputButton({
  title,
  description,
  pane,
  renderOutput,
  tooltipUrl,
}: {
  title: string;
  description: string;
  pane: Pane;
  renderOutput: PaneRenderOutput | undefined;
  tooltipUrl?: string;
}) {
  assert(pane.type === PaneType.Evaluate);
  const isSelected = areRenderOutputTypesEqual(pane.renderOutput, renderOutput);
  return (
    <div className="relative">
      <button
        onClick={() => {
          if (pane.renderOutput?.[0] !== renderOutput?.[0]) {
            canvasDispatch([
              ActionType.SetPaneRenderOutput,
              {
                paneId: pane.id,
                renderOutput,
              },
            ]);
          }
          if (renderOutput !== undefined && renderOutput[0] === "react") {
            canvasDispatch([
              ActionType.AddPaneBabelPlugin,
              {
                paneId: pane.id,
                babelPlugin: "transform-react-jsx",
              },
            ]);
          }
        }}
        className={classNames(
          "block text-left w-full px-2 py-2 rounded mb-2",
          isSelected ? "dialog-button-selected" : "dialog-button"
        )}
      >
        <div className="font-semibold">{title}</div>
        <div>{description}</div>
      </button>
      {tooltipUrl !== undefined ? (
        <a
          href={tooltipUrl}
          target="_blank"
          rel="noreferrer"
          className={classNames(
            "absolute top-0.5 right-0.5 p-1 opacity-50 hover:opacity-100 transition-opacity",
            isSelected ? "text-neutral-0" : "text-neutral-400"
          )}
        >
          <FeatherIcon icon="help-circle" size="md" />
        </a>
      ) : null}
    </div>
  );
}

function SettingsExpressionTypeButton({
  title,
  description,
  pane,
  expressionType,
  buttonRef,
}: {
  title: string;
  description: string;
  pane: Pane;
  expressionType: EvaluateExpressionType;
  buttonRef?: React.MutableRefObject<HTMLButtonElement | null>;
}) {
  assert(pane.type === PaneType.Evaluate);
  return (
    <button
      onClick={() => {
        canvasDispatch([
          ActionType.SetPaneExpressionType,
          {
            paneId: pane.id,
            expressionType,
          },
        ]);
      }}
      className={classNames(
        "block text-left w-full px-2 py-2 rounded mb-2",
        (pane.expressionType ?? EvaluateExpressionType.Expression) ===
          expressionType
          ? "dialog-button-selected"
          : "dialog-button"
      )}
      ref={buttonRef}
    >
      <div className="font-semibold">{title}</div>
      <div>{description}</div>
    </button>
  );
}

function AddImportBabelPluginForm({
  paneId,
  onClose,
}: {
  paneId: string;
  onClose: () => void;
}) {
  const [value, setValue] = useState("");

  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div>
      <input
        type="text"
        value={value}
        placeholder="plugin url to import"
        onChange={(e) => {
          setValue(e.target.value);
        }}
        className="bg-neutral-100 focus:bg-neutral-50 rounded-sm pl-2 py-2 block w-full mb-2 transition"
        ref={inputRef}
      />
      <div className="flex justify-end">
        <button
          onClick={() => {
            canvasDispatch([
              ActionType.AddPaneBabelPlugin,
              {
                paneId,
                babelPlugin: ["import", value],
              },
            ]);
            onClose();
          }}
          disabled={value.trim() === ""}
          className="px-4 py-2 font-medium bg-primary-500 text-neutral-0 rounded transition mr-2 disabled:opacity-50"
        >
          add plugin
        </button>
        <button
          onClick={() => {
            onClose();
          }}
          className="p-1 text-neutral-400 hover:text-neutral-500 transition"
        >
          cancel
        </button>
      </div>
    </div>
  );
}

function AddImportBabelPluginButton({
  paneId,
  scrollContainerRef,
}: {
  paneId: string;
  scrollContainerRef: React.MutableRefObject<HTMLDivElement | null>;
}) {
  const [shouldShowForm, setShouldShowForm] = useState(false);
  useEffect(() => {
    if (shouldShowForm) {
      scrollContainerRef.current?.scrollTo({
        top: 999999,
        behavior: "smooth",
      });
    }
  }, [shouldShowForm]);
  if (shouldShowForm) {
    return (
      <AddImportBabelPluginForm
        paneId={paneId}
        onClose={() => {
          setShouldShowForm(false);
        }}
      />
    );
  }
  return (
    <button onClick={() => setShouldShowForm(true)} className="button">
      add plugin
    </button>
  );
}

const PaneEvaluateComponentSettings = observer(
  ({
    paneId,
    paneComputed,
  }: {
    paneId: string;
    paneComputed: IComputedValue<
      Extract<Pane, { type: PaneType.Evaluate }> | undefined
    >;
  }) => {
    const pane = paneComputed.get();
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const expressionButtonRef = useRef<HTMLButtonElement | null>(null);
    useEffect(() => {
      expressionButtonRef.current?.focus();
    }, []);

    if (pane === undefined) {
      return null;
    }

    return (
      <div
        className="h-[21rem] bg-neutral-0 shadow-lg transition-opacity transform flex flex-col rounded select-none"
        ref={scrollContainerRef}
      >
        <div className="flex shrink-0 items-center justify-between h-8 pl-2 pr-1 border-b border-neutral-150">
          <span className="text-neutral-400">eval pane settings</span>
          <PopoverPrimitive.Close asChild={true}>
            <button className="flex p-1 text-neutral-300 hover:text-neutral-400 transition">
              <FeatherIcon icon="x" size="md" />
            </button>
          </PopoverPrimitive.Close>
        </div>
        <div className="grow overflow-auto px-2 pt-2 pb-4">
          <div className="flex items-center text-sm font-medium mb-2">
            editor value
            <a
              href="https://www.notion.so/Eval-pane-settings-0795337b01b9404cbd8ff7cdf4b3583d#3d91159569ff4cb690eba399e57eb6ac"
              className="flex text-neutral-300 hover:text-neutral-400 transition-colors ml-2"
              target="_blank"
              rel="noreferrer"
            >
              <FeatherIcon icon="help-circle" size="md" />
            </a>
          </div>
          <div className="mb-4">
            <SettingsExpressionTypeButton
              pane={pane}
              expressionType={EvaluateExpressionType.Expression}
              title="expression"
              description="editor value is returned"
              buttonRef={expressionButtonRef}
            />
            <SettingsExpressionTypeButton
              pane={pane}
              expressionType={EvaluateExpressionType.FunctionBody}
              title="function body"
              description="you write the return statement"
            />
            <SettingsExpressionTypeButton
              pane={pane}
              expressionType={EvaluateExpressionType.Text}
              title="text"
              description="editor value is treated as text"
            />
          </div>
          <div className="text-sm font-medium mb-1">render output</div>
          <div className="mb-2">
            <a
              href="https://www.notion.so/Eval-pane-settings-0795337b01b9404cbd8ff7cdf4b3583d#1d246a33ecc14eec97c830821379228b"
              target="_blank"
              rel="noreferrer"
              className="text-neutral-400 hover:text-neutral-500"
            >
              {"→ "}
              documentation and examples
            </a>
          </div>
          <div className="mb-4">
            <SettingsRenderOutputButton
              pane={pane}
              renderOutput={undefined}
              title="default"
              description="render JS expression"
            />
            <SettingsRenderOutputButton
              pane={pane}
              renderOutput={["table"]}
              title="table"
              description="render array as table"
            />
            <SettingsRenderOutputButton
              pane={pane}
              renderOutput={["dom"]}
              title="DOM"
              description="set DOM node as child"
            />
            <SettingsRenderOutputButton
              pane={pane}
              renderOutput={["react"]}
              title="React"
              description="render ReactElement"
              tooltipUrl="https://www.notion.so/Eval-pane-settings-0795337b01b9404cbd8ff7cdf4b3583d#276930e6bce64ae8a0d0292ef6fbe80f"
            />
            <SettingsRenderOutputButton
              pane={pane}
              renderOutput={["html"]}
              title="HTML"
              description="set string as innerHTML"
            />
            <SettingsRenderOutputButton
              pane={pane}
              renderOutput={["text"]}
              title="text"
              description="render string as multiline text"
            />
            <SettingsRenderOutputButton
              pane={pane}
              renderOutput={["graphviz"]}
              title="graphviz"
              description="render dot string as graph"
            />
            <SettingsRenderOutputButton
              pane={pane}
              renderOutput={["custom"]}
              title="custom"
              description="bring your own renderer!"
              tooltipUrl="https://www.notion.so/Eval-pane-settings-0795337b01b9404cbd8ff7cdf4b3583d#5ffa52c522c84bfc966c28f0302258fc"
            />
          </div>
          <div className="text-sm font-medium mb-2">babel plugins</div>
          <div className="mb-2">
            <label className="flex items-center mb-1">
              <span className="font-semibold mr-2">transform-react-jsx</span>
              <input
                type="checkbox"
                onChange={(e) => {
                  const checked = e.target.checked;
                  if (checked) {
                    canvasDispatch([
                      ActionType.AddPaneBabelPlugin,
                      {
                        paneId: pane.id,
                        babelPlugin: "transform-react-jsx",
                      },
                    ]);
                  } else {
                    canvasDispatch([
                      ActionType.RemovePaneBabelPlugin,
                      {
                        paneId: pane.id,
                        babelPlugin: "transform-react-jsx",
                      },
                    ]);
                  }
                }}
                checked={pane.babelPlugins?.includes("transform-react-jsx")}
              />
            </label>
            <div className="text-neutral-400 pr-2">
              transforms JSX to React.createElement
            </div>
          </div>
          <div>
            <div className="font-semibold mb-1">custom babel plugins</div>
            {pane.babelPlugins !== undefined &&
            pane.babelPlugins.filter((p) => Array.isArray(p)).length > 0 ? (
              <div className="mb-2">
                {pane.babelPlugins.map((babelPlugin) => {
                  if (!Array.isArray(babelPlugin)) {
                    return null;
                  }
                  const [, url] = babelPlugin;
                  return (
                    <div
                      className="flex items-start -mx-1 px-1 rounded-sm hover:bg-neutral-100 transition relative"
                      key={url}
                    >
                      <div className="overflow-hidden py-1 break-words grow">
                        {url}
                      </div>
                      <button
                        onClick={() => {
                          canvasDispatch([
                            ActionType.RemovePaneBabelPlugin,
                            { paneId: pane.id, babelPlugin: babelPlugin },
                          ]);
                        }}
                        className="flex p-0.5 relative -right-0.5 top-px text-neutral-400 hover:text-red-400 transition"
                      >
                        <FeatherIcon icon="x" size="md" />
                      </button>
                      <div className="absolute bottom-0 h-px bg-neutral-100 left-1 right-1" />
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
          <div>
            <AddImportBabelPluginButton
              paneId={pane.id}
              scrollContainerRef={scrollContainerRef}
            />
          </div>
        </div>
      </div>
    );
  }
);

export const PaneEvaluateComponentSettingsButton = observer(
  ({
    paneId,
    paneComputed,
  }: {
    paneId: string;
    paneComputed: IComputedValue<
      Extract<Pane, { type: PaneType.Evaluate }> | undefined
    >;
  }) => {
    const showSettings = computed(
      () => showPaneSettingsPaneIdAtom.value === paneId
    ).get();
    const wasSettingsShownRecently = useRef(showSettings);
    useEffect(() => {
      if (showSettings) {
        wasSettingsShownRecently.current = true;
      } else {
        const timeout = setTimeout(() => {
          wasSettingsShownRecently.current = false;
        }, 100);
        return () => {
          clearTimeout(timeout);
        };
      }
    }, [showSettings]);

    return (
      <>
        <Tooltip
          content={
            <div className="flex gap-2 items-center">
              <span>settings</span>
              <div className="keyboard-shortcut-tooltip">
                <span>⌃</span>
                <span>⌘</span>
                <span>
                  <span className="relative -top-1">,</span>
                </span>
              </div>
            </div>
          }
        >
          <PopoverPrimitive.Trigger asChild={true}>
            <button
              className={classNames(
                "flex transition-colors p-1 ml-2 -mr-2 rounded",
                computed(() => {
                  const pane = paneComputed.get();
                  return (
                    pane !== undefined &&
                    ((pane.babelPlugins !== undefined &&
                      pane.babelPlugins.length > 0) ||
                      pane.renderOutput !== undefined ||
                      pane.expressionType !== undefined)
                  );
                }).get()
                  ? "text-accent-400 hover:text-accent-500"
                  : "text-neutral-400 hover:text-neutral-600"
              )}
              onFocus={(e) => {
                if (wasSettingsShownRecently.current) {
                  // prevent tooltip from showing when popover closes
                  e.preventDefault();
                }
              }}
            >
              <FeatherIcon icon="sliders" size="sm" />
            </button>
          </PopoverPrimitive.Trigger>
        </Tooltip>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            side="left"
            align="start"
            sideOffset={8}
            className="popover-scale-in"
          >
            <PaneEvaluateComponentSettings
              paneId={paneId}
              paneComputed={paneComputed}
            />
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </>
    );
  }
);
